import { useTranslation } from 'react-i18next';
import { BoundedContent, Box } from '@vp/swan';
import LogomakerAnywhere from '../../components/LogomakerAnywhere/LogomakerAnywhere';
import LogoProductTiles from '../../components/LogoProductTiles/LogoProductTiles';
import { sanitiseLocale } from '../../i18n/config';
import { isGenAiLogomakerAvailable } from '../../utils/featureAvailability';
import LogoGallery from '../LogomakerGenAiPagePhase1/components/LogoGallery/LogoGallery';
import BlogPosts from './components/BlogPosts/BlogPosts';
import Faq from './components/Faq/Faq';
import Hero from './components/Hero/Hero';
import ImageTiles from './components/ImageTiles/ImageTiles';
import RelatedIdeas from './components/RelatedIdeas/RelatedIdeas';
import type { BlogPost } from '../../utils/blog';
import type { RelatedKeywordData } from './components/RelatedIdeas/RelatedIdeas';
import type { KeywordData } from './content/keyword-data';
import type { Design } from './content/logo-inspiration';

interface LogoInspirationPageProps {
    keywordData?: KeywordData;
    relatedKeywords: RelatedKeywordData[];
    designs: Design[];
    blogPosts: BlogPost[];
}

const LogoInspirationPage = ({
    keywordData,
    relatedKeywords,
    designs,
    blogPosts,
}: LogoInspirationPageProps) => {
    const { i18n } = useTranslation();
    const locale = sanitiseLocale(i18n.language);
    const [heroImageLogo, ...exampleImageLogos] = designs;
    const { keyword, name, longName } = keywordData ?? {};
    const longNameSpaced = longName ? `${longName} ` : '';

    const classicLogoProductTiles = {
        logomaker: {
            title: `Create your ${longNameSpaced}logo easily and at your own pace`,
            description: `No design skills required. Get a free ${longNameSpaced}logo that's ready to use in digital and print, or use it as inspiration for a designer.`,
        },
        'logo-essential': {
            title: `Hire a designer to create your unique ${longNameSpaced}logo`,
            description: `Need help from a professional? Choose a freelance designer to collaborate with and get a unique ${longNameSpaced}logo at an affordable price.`,
        },
        'logo-contest': {
            title: `Get dozens of ${longNameSpaced}logo ideas, choose your favorite`,
            description: `Get multiple, custom ${longNameSpaced}logos created by our professional design community - then choose the logo you love or get your money back.`,
        },
    };

    const genAiLogoProductTiles = {
        logomaker: {
            title: `Create a free, AI ${longNameSpaced}logo you'll love`,
            description: `Get a ${longNameSpaced}logo made just for you with our intuitive AI. Built for businesses like yours, it's easy to refine ideas - then get perfect, print-ready files.`,
        },
        'logo-essential': {
            title: `Work with a designer for a handcrafted ${longNameSpaced}logo`,
            description: `Need help from a professional? Collaborate with a vetted designer of your choosing and get a completely handcrafted ${longNameSpaced}logo at an affordable price.`,
        },
        'logo-contest': {
            title: `Get dozens of ideas, choose your favorite ${longNameSpaced}logo`,
            description: `Get multiple, handcrafted ${longNameSpaced}logo ideas created by our professional design community at 99designs - then choose the logo you love or get your money back.`,
        },
    };

    return (
        <>
            <Hero design={heroImageLogo} keywordLongName={longName} keywordName={name} />
            <BoundedContent marginY="between-sections">
                {isGenAiLogomakerAvailable(locale) ? (
                    <LogoGallery
                        title="Get an AI logo you'll love, instantly."
                        description="Create a professional logo in minutes with our intuitive AI. No matter your industry, get a print-ready logo that looks sharp everywhere your brand shows up."
                    />
                ) : (
                    <LogomakerAnywhere
                        keyword={longName}
                        keywordLongName={longName}
                        customCtaText={`Explore more ${longNameSpaced}logos`}
                    />
                )}
            </BoundedContent>
            <BoundedContent marginBottom="between-sections">
                <ImageTiles
                    designs={exampleImageLogos}
                    keyword={keyword}
                    keywordLongName={longName}
                />
            </BoundedContent>
            <BoundedContent marginBottom="between-sections">
                <RelatedIdeas
                    keyword={keyword}
                    longName={longName}
                    relatedKeywords={relatedKeywords}
                />
            </BoundedContent>
            <BoundedContent overflow="hidden" bgc="warm-white">
                <Box marginY="between-sections">
                    <LogoProductTiles
                        trackingCategory="Logo Ideas"
                        title={`Create your ${longNameSpaced}logo on any budget`}
                        description={`Whether you want to do it yourself with our free Logomaker or get a logo from scratch by working with a professional designer, build your unique brand with one of our affordable ${longNameSpaced}logo design options.`}
                        customProductTileCopy={
                            isGenAiLogomakerAvailable(locale)
                                ? genAiLogoProductTiles
                                : classicLogoProductTiles
                        }
                        showLogomaker
                        showLogoContest
                    />
                </Box>
            </BoundedContent>
            {blogPosts && blogPosts.length > 0 && (
                <BoundedContent marginY="between-sections">
                    <BlogPosts blogPosts={blogPosts} keywordLongName={longName} />
                </BoundedContent>
            )}
            <Faq longNameSpaced={longNameSpaced} />
        </>
    );
};

export default LogoInspirationPage;
